'use client';

import {
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  IconButton,
  CircularProgress,
} from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';
import { useMemo, useRef, useState } from 'react';

import OrderForm, { OrderFormHandle } from './OrderForm';

import { OrderDto, OrderActionEnum, CostCenterDto } from 'desk-common';

import useUser from '../../auth/useUser';
import { useCostCenters } from '../../hooks/useCostCenters';
import { useProducts } from '../../hooks/useProducts';
import OrderModalFooter from './OrderModalFooter';
import useOrderAvailableActions from '../../hooks/useOrderAvailableActions';

type Props = {
  open: boolean;
  onClose: () => void;
  order: OrderDto | null;
};

export default function CreateOrderModal({ open, onClose, order }: Props) {
  const formRef = useRef<OrderFormHandle | null>(null);
  const [total, setTotal] = useState(0);
  const [pendingAction, setPendingAction] = useState<OrderActionEnum | null>(
    null
  );
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [selectedCostCenterId, setSelectedCostCenterId] = useState<
    number | null
  >(order?.costCenterId ?? null);
  const [selectedProductsCount, setSelectedProductsCount] = useState<
    number | null
  >(order?.items.length ?? null);

  const user = useUser();
  const { availableActions, showDraftButtons } = useOrderAvailableActions(
    order,
    user
  );

  const { data: costCenters = [], isLoading: isLoadingCC } = useCostCenters({
    active: true,
    findByUser: true,
  });

  const { data: products = [], isLoading: isLoadingProd } = useProducts({
    ...(!order ? { active: true } : {}),
  });

  const isLoading = isLoadingCC || isLoadingProd;

  const validCostCenters = useMemo(() => {
    const costCenter = order?.costCenter;
    if (!costCenter || costCenters.some((c) => c.id === costCenter.id)) {
      return costCenters;
    }
    return [...costCenters, costCenter as CostCenterDto];
  }, [costCenters, order]);

  const handleSubmit = async (action: OrderActionEnum) => {
    if (pendingAction || !formRef.current) {
      return;
    }
    setPendingAction(action);
    await formRef.current.submit(action);
  };

  const handleSuccess = () => {
    setTotal(0);
    setPendingAction(null);
    setHasUnsavedChanges(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 5,
            boxShadow: '0 30px 80px rgba(0,0,0,0.18)',
            overflow: 'hidden',
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 4,
          py: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background:
            'linear-gradient(135deg, rgba(25,118,210,0.08), rgba(25,118,210,0.02))',
        }}
      >
        <Typography variant="h5" fontWeight={600}>
          {order ? 'Editar Pedido' : 'Crear Pedido'}
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          px: 4,
          py: 4,
          backgroundColor: '#fafafa',
          display: 'flex',
          minHeight: isLoading ? '500px' : 'auto',
          flexDirection: 'column',
          justifyContent: isLoading ? 'center' : 'stretch',
          alignItems: isLoading ? 'center' : 'stretch',
        }}
      >
        {isLoading ? (
          <CircularProgress />
        ) : (
          <OrderForm
            ref={formRef}
            order={order}
            onSuccess={handleSuccess}
            onSubmitSettled={() => setPendingAction(null)}
            onTotalChange={setTotal}
            onUnsavedChangesChange={setHasUnsavedChanges}
            onCostCenterChange={setSelectedCostCenterId}
            onItemCountChange={setSelectedProductsCount}
            costCenters={validCostCenters}
            products={products}
          />
        )}
      </DialogContent>

      <OrderModalFooter
        isLoading={isLoading}
        total={total}
        availableActions={availableActions}
        pendingAction={pendingAction}
        order={order}
        hasUnsavedChanges={hasUnsavedChanges}
        isSendButtonEnabled={
          Boolean(selectedCostCenterId) && Boolean(selectedProductsCount)
        }
        showDraftButtons={showDraftButtons}
        onSubmit={handleSubmit}
      />
    </Dialog>
  );
}
